import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Paper, CircularProgress, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getOrgTokens, ORG_PALETTE } from "../../../theme/style/organizationPalette";
import { M3Button, Chip } from "../../../components/m3";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ActionableLeversTable from "./ActionableLeversTable";
import LeverDetailSheet from "./LeverDetailSheet";
import { rankTopEmissions } from "./actionableCalculators";
import { Supply_chain } from "../../../db_access/supply_chain";
import { Structure } from "../../../db_access/structure";

/**
 * Normalizes backend response to support both camelCase and snake_case properties seamlessly.
 */
export const normalizeActionableData = (data) => {
  if (!data || !data.categories) return null;
  const categories = data.categories.map((c) => ({
    rowId: c.rowId || c.row_id,
    category: c.category,
    description: c.description || c.category,
    baselineTco2e: Number(c.baselineTco2e ?? c.baseline_tco2e ?? 0),
    sharePct: Number(c.sharePct ?? c.share_pct ?? 0),
    archetype: c.archetype || "other",
    driver: c.driver || "emissions = activity x emission factor",
    levers: (c.levers || []).map((lev) => ({
      leverId: lev.leverId || lev.lever_id,
      leverName: lev.leverName || lev.lever_name,
      variableChange: lev.variableChange || lev.variable_change,
      calculatorId: lev.calculatorId || lev.calculator_id,
      inputs: lev.inputs || {},
      pctReductionStr: lev.pctReductionStr || lev.pct_reduction_str,
      minPct: Number(lev.minPct ?? lev.min_pct ?? 0),
      maxPct: Number(lev.maxPct ?? lev.max_pct ?? 0),
      minReductionTco2e: Number(lev.minReductionTco2e ?? lev.min_reduction_tco2e ?? 0),
      maxReductionTco2e: Number(lev.maxReductionTco2e ?? lev.max_reduction_tco2e ?? 0),
      finding: lev.finding || "",
      action: lev.action || "",
      feasibility: lev.feasibility || "High",
      serviceRisk: lev.serviceRisk || lev.service_risk || "Low",
      dataNeeded: lev.dataNeeded || lev.data_needed || [],
    })),
    combined: {
      pctReductionStr: c.combined?.pctReductionStr || c.combined?.pct_reduction_str || "0%",
      minPct: Number(c.combined?.minPct ?? c.combined?.min_pct ?? 0),
      maxPct: Number(c.combined?.maxPct ?? c.combined?.max_pct ?? 0),
      minReductionTco2e: Number(c.combined?.minReductionTco2e ?? c.combined?.min_reduction_tco2e ?? 0),
      maxReductionTco2e: Number(c.combined?.maxReductionTco2e ?? c.combined?.max_reduction_tco2e ?? 0),
    },
  }));

  const summary = {
    totalTop5BaselineTco2e: Number(data.summary?.totalTop5BaselineTco2e ?? data.summary?.total_top5_baseline_tco2e ?? 0),
    totalMinReductionTco2e: Number(data.summary?.totalMinReductionTco2e ?? data.summary?.total_min_reduction_tco2e ?? 0),
    totalMaxReductionTco2e: Number(data.summary?.totalMaxReductionTco2e ?? data.summary?.total_max_reduction_tco2e ?? 0),
    totalPctReductionStr: data.summary?.totalPctReductionStr || data.summary?.total_pct_reduction_str || "",
    totalMinPct: Number(data.summary?.totalMinPct ?? data.summary?.total_min_pct ?? 0),
    totalMaxPct: Number(data.summary?.totalMaxPct ?? data.summary?.total_max_pct ?? 0),
    categoriesCount: Number(data.summary?.categoriesCount ?? data.summary?.categories_count ?? categories.length),
    leversCount: Number(data.summary?.leversCount ?? data.summary?.levers_count ?? categories.reduce((a, b) => a + b.levers.length, 0)),
  };

  return {
    ...data,
    categories,
    summary,
    totalPortfolioTco2e: Number(data.totalPortfolioTco2e ?? data.total_portfolio_tco2e ?? summary.totalTop5BaselineTco2e),
  };
};

const ActionableInsightsView = ({
  organizationId,
  organizationType,
  emissions = [],
  emissionDetails = [],
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [data, setData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedLever, setSelectedLever] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const timerRef = useRef(null);

  const organization = useMemo(() => {
    if (organizationType === "supply_chain") return Supply_chain;
    return Structure;
  }, [organizationType]);

  const storageKey = `arivu_actionable_insights_${organizationType || "structure"}_${organizationId || "default"}`;

  // Ranked baseline preview for empty state / cards
  const rankedPreview = useMemo(() => {
    return rankTopEmissions(emissions, emissionDetails, 5);
  }, [emissions, emissionDetails]);

  // Load saved insights on mount or when organization changes
  useEffect(() => {
    let isCancelled = false;
    setData(null);
    setHasGenerated(false);
    setErrorMessage(null);

    // 1. Try local storage cache
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const normalized = normalizeActionableData(parsed);
        if (normalized && normalized.categories && normalized.categories.length > 0) {
          setData(normalized);
          setHasGenerated(true);
          return;
        }
      }
    } catch (e) {
      console.warn("Error reading cached actionable insights:", e);
    }

    // 2. Try loading from backend API document store
    if (organizationId && organization?.get_actionable_insight) {
      organization
        .get_actionable_insight(organizationId)
        .then((res) => {
          if (res && res.ok) return res.json();
          return null;
        })
        .then((backendData) => {
          if (!isCancelled && backendData) {
            const normalized = normalizeActionableData(backendData);
            if (normalized && normalized.categories && normalized.categories.length > 0) {
              setData(normalized);
              setHasGenerated(true);
              try {
                localStorage.setItem(storageKey, JSON.stringify(normalized));
              } catch (e) { }
            }
          }
        })
        .catch(() => {
          // No saved file yet
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [organizationId, organization, storageKey]);

  const startTimer = () => {
    setElapsed(0);
    setIsGenerating(true);
    const start = Date.now();
    timerRef.current = setInterval(() => {
      setElapsed(Date.now() - start);
    }, 100);
  };

  const endTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setIsGenerating(false);
  };

  // Perform genuine AI analysis for this organization's actual emissions rows
  const handleGenerate = () => {
    if (!organizationId || isGenerating) return;
    setErrorMessage(null);
    startTimer();

    if (!organization?.generate_actionable_insight) {
      endTimer();
      setErrorMessage("Generate endpoint is not available for this organization type.");
      return;
    }

    organization
      .generate_actionable_insight(organizationId)
      .then(async (res) => {
        if (!res) throw new Error("No response from server");
        const json = await res.json();
        if (!res.ok || json.status === "error") {
          throw new Error(json.message || `Server error (${res.status})`);
        }
        return json;
      })
      .then((freshData) => {
        const normalized = normalizeActionableData(freshData);
        if (normalized && normalized.categories && normalized.categories.length > 0) {
          setData(normalized);
          setHasGenerated(true);
          setErrorMessage(null);
          try {
            localStorage.setItem(storageKey, JSON.stringify(normalized));
          } catch (e) { }
        } else {
          throw new Error("No actionable insights returned from AI.");
        }
      })
      .catch((err) => {
        console.error("[Actionable Insights] AI generation error:", err);
        setErrorMessage(
          err.message ||
          "Failed to generate actionable insights via AI. Please check server logs and API configuration."
        );
      })
      .finally(() => {
        endTimer();
      });
  };

  const handleOpenLever = (lever, category) => {
    setSelectedLever(lever);
    setSelectedCategory(category);
  };

  const handleCloseLever = () => {
    setSelectedLever(null);
    setSelectedCategory(null);
  };

  const summary = data?.summary;
  const top5Baseline = summary?.totalTop5BaselineTco2e || rankedPreview.topRows.reduce((a, b) => a + b.baselineTco2e, 0);
  const portfolioTotal = data?.totalPortfolioTco2e || rankedPreview.totalPortfolioTco2e || top5Baseline || 1;
  const top5Share = Math.min(Math.round((top5Baseline / portfolioTotal) * 100), 100);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, animation: "org-fadeInUp 0.4s ease" }}>
      {/* ── Page Header Card ────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: ORG_PALETTE.radius.card,
          border: `1px solid ${tok.border.card}`,
          bgcolor: tok.bg.card,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, fontSize: "20px", color: tok.font.tableTitle, mb: 0.5 }}>
              {t("Data to Actionable Insight")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "13px" }}>
              {t("Specific, quantified, achievable reduction insights built on operating variables for top emitting activities.")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {elapsed > 0 && (
              <Typography variant="caption" sx={{ color: "text.secondary", mr: 1 }}>
                {isGenerating
                  ? `${t("AI analyzing variables & computing...")} ${(elapsed / 1000).toFixed(1)}s`
                  : `${t("Quantified in")} ${(elapsed / 1000).toFixed(1)}s`}
              </Typography>
            )}

            {/* Generate / Regenerate Button */}
            {!readOnly && (
              <M3Button
                variant="filled"
                size="md"
                icon={
                  isGenerating ? (
                    <CircularProgress size={16} sx={{ color: "#ffffff" }} />
                  ) : hasGenerated ? (
                    <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
                  )
                }
                onClick={handleGenerate}
                disabled={isGenerating || emissions.length === 0}
              >
                {isGenerating
                  ? t("Generating AI Insights...")
                  : hasGenerated
                    ? t("Regenerate Insights")
                    : t("Generate Actionable Insights")}
              </M3Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* ── Independent Metric Cards Grid ───────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2.5,
        }}
      >
        {/* Metric Card 1: Top 5 Emitters Baseline */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: ORG_PALETTE.radius.card,
            border: `1px solid ${isDark ? ORG_PALETTE.metricCards.chain.borderDark : "rgba(14, 165, 233, 0.25)"}`,
            bgcolor: isDark ? ORG_PALETTE.metricCards.chain.bgDark : ORG_PALETTE.metricCards.chain.bg,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxShadow: tok.shadow.card,
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: ORG_PALETTE.animation.cardHover,
              boxShadow: tok.shadow.cardHover,
            },
          }}
        >
          <Typography variant="caption" sx={{ color: isDark ? ORG_PALETTE.metricCards.chain.accent : "#0284c7", fontWeight: 600, display: "block", mb: 1 }}>
            {t("Top 5 Emitters Baseline")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: isDark ? ORG_PALETTE.metricCards.chain.iconColorDark : "#0369a1", mb: 0.5 }}>
            {top5Baseline.toLocaleString()}{" "}
            <span style={{ fontSize: "14px", fontWeight: 400, color: isDark ? "rgba(147, 197, 253, 0.75)" : "#0284c7" }}>tCO₂e</span>
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? "rgba(147, 197, 253, 0.75)" : "#0284c7", fontWeight: 500 }}>
            {`${top5Share}% ${t("of total organization emissions")}`}
          </Typography>
        </Paper>

        {/* Metric Card 2: Potential Reduction Range */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: ORG_PALETTE.radius.card,
            border: `1px solid ${isDark ? ORG_PALETTE.metricCards.cost.borderDark : "rgba(34, 197, 94, 0.25)"}`,
            bgcolor: isDark ? ORG_PALETTE.metricCards.cost.bgDark : ORG_PALETTE.metricCards.cost.bg,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxShadow: tok.shadow.card,
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: ORG_PALETTE.animation.cardHover,
              boxShadow: tok.shadow.cardHover,
            },
          }}
        >
          <Typography variant="caption" sx={{ color: isDark ? ORG_PALETTE.metricCards.cost.accent : "#16a34a", fontWeight: 600, display: "block", mb: 1 }}>
            {t("Potential Reduction Range")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: isDark ? ORG_PALETTE.metricCards.cost.iconColorDark : "#15803d", mb: 0.5 }}>
            {summary
              ? `${summary.totalMinReductionTco2e.toLocaleString()} – ${summary.totalMaxReductionTco2e.toLocaleString()}`
              : "—"}{" "}
            <span style={{ fontSize: "14px", fontWeight: 400, color: isDark ? "rgba(167, 243, 208, 0.75)" : "#16a34a" }}>tCO₂e</span>
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? "rgba(167, 243, 208, 0.75)" : "#16a34a", fontWeight: 600 }}>
            {summary?.totalPctReductionStr || t("Pending analysis")}
          </Typography>
        </Paper>

        {/* Metric Card 3: Operational Levers Active */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: ORG_PALETTE.radius.card,
            border: `1px solid ${isDark ? ORG_PALETTE.metricCards.shield.borderDark : "rgba(168, 85, 247, 0.25)"}`,
            bgcolor: isDark ? ORG_PALETTE.metricCards.shield.bgDark : ORG_PALETTE.metricCards.shield.bg,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxShadow: tok.shadow.card,
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: ORG_PALETTE.animation.cardHover,
              boxShadow: tok.shadow.cardHover,
            },
          }}
        >
          <Typography variant="caption" sx={{ color: isDark ? ORG_PALETTE.metricCards.shield.accent : "#7c3aed", fontWeight: 600, display: "block", mb: 1 }}>
            {t("Operational Levers Active")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: isDark ? ORG_PALETTE.metricCards.shield.iconColorDark : "#6d28d9", mb: 0.5 }}>
            {summary?.leversCount || 0}{" "}
            <span style={{ fontSize: "14px", fontWeight: 400, color: isDark ? "rgba(221, 214, 254, 0.75)" : "#7c3aed" }}>{t("Levers")}</span>
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? "rgba(221, 214, 254, 0.75)" : "#7c3aed", fontWeight: 500 }}>
            {`${summary?.categoriesCount || rankedPreview.topRows.length} ${t("top emitting categories analyzed")}`}
          </Typography>
        </Paper>

        {/* Metric Card 4: Quantification Standard */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: ORG_PALETTE.radius.card,
            border: `1px solid ${isDark ? ORG_PALETTE.metricCards.gwp.borderDark : "rgba(245, 158, 11, 0.25)"}`,
            bgcolor: isDark ? ORG_PALETTE.metricCards.gwp.bgDark : ORG_PALETTE.metricCards.gwp.bg,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxShadow: tok.shadow.card,
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: ORG_PALETTE.animation.cardHover,
              boxShadow: tok.shadow.cardHover,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="caption" sx={{ color: isDark ? ORG_PALETTE.metricCards.gwp.accent : "#b45309", fontWeight: 600 }}>
              {t("Quantification Standard")}
            </Typography>
            <Chip size="sm" tone={isDark ? "neutral" : "warning"}>
              {t("Deterministic")}
            </Chip>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "16px", color: isDark ? ORG_PALETTE.metricCards.gwp.iconColorDark : "#92400e" }}>
            {t("Operating Variables")}
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? "rgba(253, 230, 138, 0.75)" : "#b45309", mt: 0.5, fontWeight: 500 }}>
            {t("8 mathematical calculators applied")}
          </Typography>
        </Paper>
      </Box>

      {/* ── Error Banner (if AI call failed) ───────────────────────── */}
      {errorMessage && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: ORG_PALETTE.radius.card,
            border: "1px solid #fca5a5",
            bgcolor: isDark ? "rgba(239, 68, 68, 0.1)" : "#fef2f2",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ErrorOutlineOutlinedIcon sx={{ color: "#ef4444" }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#b91c1c" }}>
                {t("AI Insight Generation Failed")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#7f1d1d", fontSize: "13px" }}>
                {errorMessage}
              </Typography>
            </Box>
          </Box>
          <M3Button variant="outlined" size="sm" onClick={handleGenerate} disabled={isGenerating}>
            {t("Retry AI Analysis")}
          </M3Button>
        </Paper>
      )}

      {/* ── Content: Empty State vs Quantified Levers Table ─────────── */}
      {!hasGenerated || !data || !data.categories || data.categories.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: ORG_PALETTE.radius.card,
            border: `1px dashed ${tok.border.divider}`,
            bgcolor: isDark ? "rgba(255,255,255,0.01)" : "#fafafa",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: tok.font.tableTitle }}>
            {t("Actionable Insights Ready to Analyze")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 560, lineHeight: 1.6 }}>
            {t(
              "We have identified the top 5 emitter categories from this inventory. Click 'Generate Actionable Insights' to run the emissions-reduction AI model, evaluate operational variable shifts, and quantify min/max reductions across the 8 mathematical calculators."
            )}
          </Typography>
          {!readOnly && (
            <M3Button
              variant="filled"
              size="lg"
              icon={
                isGenerating ? (
                  <CircularProgress size={18} sx={{ color: "#ffffff" }} />
                ) : (
                  <AutoAwesomeOutlinedIcon sx={{ fontSize: 20 }} />
                )
              }
              onClick={handleGenerate}
              disabled={isGenerating || emissions.length === 0}
              sx={{ mt: 1 }}
            >
              {isGenerating ? t("Quantifying Levers via AI...") : t("Generate Actionable Insights")}
            </M3Button>
          )}
        </Paper>
      ) : (
        <ActionableLeversTable
          data={data}
          categories={data.categories}
          summary={data.summary}
          onSelectLever={handleOpenLever}
        />
      )}

      {/* ── SideSheet for Lever Details & Formula Inspection ────────── */}
      <LeverDetailSheet
        open={Boolean(selectedLever)}
        onClose={handleCloseLever}
        lever={selectedLever}
        category={selectedCategory}
      />
    </Box>
  );
};

export default ActionableInsightsView;
